if (!customElements.get('rain-cover-checkbox')) {
    customElements.define(
        'rain-cover-checkbox',
        class RainCoverCheckbox extends HTMLElement {
        constructor() {
            super();
            this.rainCoverVariantId = null;
            this.rainCoverPrice = 0;
            this.rainCoverHandle = '';
            this.rainCoverTitle = '';
            this.rainCoverTags = '';
            this.rainCoverLineKey = null;
            this.checkbox = null;
            this.isProcessing = false;
            this.lockedByMainSubmit = false;
            this.cartUpdateUnsubscriber = undefined;
            this.changeHandler = this.handleCheckboxChange.bind(this);
        }
    
        connectedCallback() {
            this.rainCoverVariantId = this.dataset.variantId;
            this.rainCoverPrice = parseInt(this.dataset.price) || 0;
            this.rainCoverHandle = this.dataset.handle;
            this.rainCoverTitle = this.dataset.title;
            this.rainCoverTags = this.dataset.productTags || '';
            this.rainCoverInCartOnRender = this.dataset.inCart === 'true';
            this.mainSubmitButton = this.dataset.sectionId
            ? document.getElementById(`ProductSubmitButton-${this.dataset.sectionId}`)
            : null;

            if (!this.rainCoverVariantId) {
            console.error('Rain cover checkbox: missing variant ID');
            return;
            }

            this.checkbox = this.querySelector('.rain-cover-checkbox__input');
            if (!this.checkbox) {
            console.error('Rain cover checkbox: checkbox input not found');
            return;
            }
    
            this.checkbox.addEventListener('change', this.changeHandler);

            if (this.mainSubmitButton) {
            this.mainSubmitObserver = new MutationObserver(() => this.syncMainSubmitLock());
            this.mainSubmitObserver.observe(this.mainSubmitButton, { attributes: true, attributeFilter: ['aria-disabled'] });
            this.syncMainSubmitLock();
            }

            if (this.checkbox.checked) {
            this.syncInitialCheckedState();
            }

            if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => this.handleExternalCartUpdate(event));
            }
        }

        disconnectedCallback() {
            if (this.checkbox) {
            this.checkbox.removeEventListener('change', this.changeHandler);
            }
            if (this.mainSubmitObserver) {
            this.mainSubmitObserver.disconnect();
            }
            if (this.cartUpdateUnsubscriber) {
            this.cartUpdateUnsubscriber();
            }
        }

        async handleExternalCartUpdate(event) {
            if (event.source === 'rain-cover-checkbox' || this.isProcessing) return;

            try {
            const existingLine = await this.findRainCoverLine();
            this.rainCoverLineKey = existingLine ? existingLine.key : null;
            this.checkbox.checked = Boolean(existingLine);
            } catch (error) {
            console.error('Error syncing rain cover state after cart update:', error);
            }
        }

        syncMainSubmitLock() {
            this.lockedByMainSubmit = this.mainSubmitButton.getAttribute('aria-disabled') === 'true';
            this.updateCheckboxDisabledState();
        }

        updateCheckboxDisabledState() {
            if (this.checkbox) {
            this.checkbox.disabled = this.isProcessing || this.lockedByMainSubmit;
            }
        }
    
        async syncInitialCheckedState() {
            if (this.rainCoverInCartOnRender) return;
    
            this.setProcessing(true);
    
            try {
            const existingLine = await this.findRainCoverLine();
            if (!existingLine) {
                await this.addToCart();
            }
            } catch (error) {
            console.error('Error syncing rain cover initial state:', error);
            } finally {
            this.setProcessing(false);
            }
        }
    
        async handleCheckboxChange(event) {
            if (this.isProcessing) return;
    
            const isChecked = event.target.checked;
            this.setProcessing(true);
    
            try {
            if (isChecked) {
                await this.addToCart();
            } else {
                await this.removeFromCart();
            }
            } catch (error) {
            console.error('Error updating rain cover in cart:', error);
            // Revert the checkbox to reflect actual cart state on failure
            this.checkbox.checked = !isChecked;
    
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
                publish(PUB_SUB_EVENTS.cartError, {
                source: 'rain-cover-checkbox',
                error: error.message
                });
            }
            } finally {
            this.setProcessing(false);
            }
        }
    
        setProcessing(isProcessing) {
            this.isProcessing = isProcessing;
            this.updateCheckboxDisabledState();

            if (this.mainSubmitButton) {
            if (isProcessing) {
                this.mainSubmitButton.setAttribute('aria-disabled', 'true');
            } else {
                this.mainSubmitButton.removeAttribute('aria-disabled');
            }
            }
        }
    
        async findRainCoverLine() {
            const response = await fetch('/cart.js');
            const cart = await response.json();

            const line = cart.items.find((item) =>
            item.properties &&
            item.properties._rain_cover === 'true' &&
            String(item.variant_id) === String(this.rainCoverVariantId)
            ) || null;

            if (line) this.rainCoverLineKey = line.key;
            return line;
        }

        async addToCart() {
            const lowestDeliveryCost = this.rainCoverTags && typeof delivery_tabs !== 'undefined' ?
            window.delivery_tabs.calculateLowestDeliveryCost(this.rainCoverTags) : null;

            const properties = { _rain_cover: 'true' };
            if (lowestDeliveryCost !== null) {
            properties['Lowest Delivery Cost'] = String(lowestDeliveryCost);
            }

            const body = { id: this.rainCoverVariantId, quantity: 1, properties };
            this.attachSections(body);

            const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body)
            });

            if (!response.ok) {
            throw new Error(`Failed to add rain cover: ${response.status}`);
            }

            const result = await response.json();
            if (result.status) {
            throw new Error(result.description || 'Failed to add rain cover to cart');
            }

            this.rainCoverLineKey = result.key || null;
            this.applyCartUpdate(result);
        }

        async removeFromCart() {
            let lineKey = this.rainCoverLineKey;

            if (!lineKey) {
            const existingLine = await this.findRainCoverLine();
            if (!existingLine) return;
            lineKey = existingLine.key;
            }

            const body = { id: lineKey, quantity: 0 };
            this.attachSections(body);

            const response = await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body)
            });

            if (!response.ok) {
            throw new Error(`Failed to remove rain cover: ${response.status}`);
            }

            const result = await response.json();
            if (result.status) {
            throw new Error(result.description || 'Failed to remove rain cover from cart');
            }

            this.rainCoverLineKey = null;
            this.applyCartUpdate(result);
        }

        attachSections(body) {
            const cartDrawer = document.querySelector('cart-drawer');
            if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
            body.sections = cartDrawer.getSectionsToRender().map((section) => section.id);
            body.sections_url = window.location.pathname;
            }
        }

        applyCartUpdate(cartResponse) {
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'rain-cover-checkbox',
                cartData: cartResponse
            });
            }

            const cartDrawer = document.querySelector('cart-drawer');
            if (!cartDrawer) return;

            if (cartResponse.sections && typeof cartDrawer.renderContents === 'function') {
            cartDrawer.renderContents(cartResponse, false);
            }

            if (typeof cartResponse.item_count === 'number') {
            cartDrawer.classList.toggle('is-empty', cartResponse.item_count === 0);
            }
        }
        }
    );
}